import { Record } from "immutable";
import moment from "moment";
import { TRANSACTION_TYPES_ENUM } from "@nuorder/nuorder-infinicept-enums";

const defaults = {
    id: undefined,
    brand_id: undefined,
    payment_account_id: undefined,
    infinicept_id: undefined,
    nuorder_transaction_id: undefined,
    currency_code: undefined,
    transaction_date: undefined,
    arn: undefined,
    authorization_code: undefined,
    authorization_source: undefined,
    authorization_response: undefined,
    authorization_amount: undefined,
    transaction_amount: undefined,
    submerchant_batch_id: undefined,
    card_bin: undefined,
    card_last_four: undefined,
    card_type: undefined,
    card_expiration_date: undefined,
    issuing_bank_id: undefined,
    interchange_fee: undefined,
    network_fees: undefined,
    assessment_fee: undefined,
    payfac_fee: undefined,
    chargeback_fee: undefined,
    commission_fee: undefined,
    source_data_event_id: undefined,
    processor_mid: undefined,
    on_hold: undefined,
    terminal_id: undefined,
    processor_transaction_id: undefined,
    submerchant_id: undefined,
    submerchant_name: undefined,
    submerchant_dba_name: undefined,
    exchange_data: undefined,
    interchange_code: undefined,
    interchange_level: undefined,
    gateway_reference_id: undefined,
    processed_date: undefined,
    settlement_date: undefined,
    fee_settlement_date: undefined,
    tip_amount: undefined,
    total_fees: undefined,
    net_amount: undefined,
    adjustment_id: undefined,
    is_forced: undefined,
    ach_return_fee: undefined,
    network: undefined,
    card_present_type: undefined,
    transaction_type: undefined,
    funding_status: undefined,
    fee_funding_status: undefined,
    submerchant_status: undefined,
    fee_profile_type: undefined,
    fee_billing_type: undefined,
    notes: undefined,
    merchant_identifiers: undefined,
    e_check: undefined,
    deposit_ids: undefined,
    created_on: undefined,
    modified_on: undefined,
    partner_reference_id: undefined,
    order_number: undefined,
    buyer_email: undefined,
    company_name: undefined,
};

export class Transaction extends Record(defaults) {
    declare id: string;
    declare brand_id: string;
    declare payment_account_id: string;
    declare infinicept_id: string;
    declare nuorder_transaction_id: string;
    declare currency_code: string;
    declare transaction_date: moment.Moment;
    declare arn: string;
    declare authorization_code: string;
    declare authorization_source: string;
    declare authorization_response: string;
    declare authorization_amount: number;
    declare transaction_amount: number;
    declare submerchant_batch_id: string;
    declare card_bin: string;
    declare card_last_four: string;
    declare card_type: string;
    declare card_expiration_date: moment.Moment;
    declare issuing_bank_id: string;
    declare interchange_fee: number;
    declare network_fees: number;
    declare assessment_fee: number;
    declare payfac_fee: number;
    declare chargeback_fee: number;
    declare commission_fee: number;
    declare source_data_event_id: number;
    declare processor_mid: string;
    declare on_hold: boolean;
    declare terminal_id: string;
    declare processor_transaction_id: string;
    declare submerchant_id: number;
    declare submerchant_name: string;
    declare submerchant_dba_name: string;
    declare exchange_data: string;
    declare interchange_code: string;
    declare interchange_level: string;
    declare gateway_reference_id: string;
    declare processed_date: moment.Moment;
    declare settlement_date: moment.Moment;
    declare fee_settlement_date: moment.Moment;
    declare tip_amount: number;
    declare total_fees: number;
    declare net_amount: number;
    declare adjustment_id: number;
    declare is_forced: boolean;
    declare ach_return_fee: number;
    declare network: number;
    declare card_present_type: number;
    declare transaction_type: number;
    declare funding_status: number;
    declare fee_funding_status: number;
    declare submerchant_status: number;
    declare fee_profile_type: number;
    declare fee_billing_type: number;
    declare notes: any;
    declare merchant_identifiers: any;
    declare e_check: any;
    declare deposit_ids: any;
    declare created_on: moment.Moment;
    declare modified_on: moment.Moment;
    declare partner_reference_id: string;
    declare order_number: string;
    declare buyer_email: string;
    declare company_name: string;

    constructor(args) {
        super({
            ...args,
            created_on: moment.utc(args.created_on),
            modified_on: moment.utc(args.modified_on),
            transaction_date: moment(args.transaction_date), // intentionally locale time for sanity checking with Infinicept
            card_expiration_date: moment.utc(args.card_expiration_date),
            processed_date: moment.utc(args.processed_date),
            settlement_date: moment.utc(args.settlement_date),
            fee_settlement_date: moment.utc(args.fee_settlement_date),
            interchange_fee: args.interchange_fee || 0,
            network_fees: args.network_fees || 0,
            assessment_fee: args.assessment_fee || 0,
            payfac_fee: args.payfac_fee || 0,
            chargeback_fee: args.chargeback_fee || 0,
            commission_fee: args.commission_fee || 0,
        });
    }

    isUnlinkedTransaction(): boolean {
        // "unlinked" meaning these transactions can be created in Adyen/Infinicept but may not be tied to
        // a specific order in NuO
        return [
            TRANSACTION_TYPES_ENUM.AdjustmentTo,
            TRANSACTION_TYPES_ENUM.AdjustmentFrom,
            TRANSACTION_TYPES_ENUM.Fee,
        ].includes(this.transaction_type);
    }

    isRefund(): boolean {
        return [
            TRANSACTION_TYPES_ENUM.Refund,
            TRANSACTION_TYPES_ENUM.AdjustmentFrom,
            TRANSACTION_TYPES_ENUM.Fee,
        ].includes(this.transaction_type);
    }

    getTotalFees(): number {
        return this.total_fees;
    }

    getNetForDeposit(): number {
        let value = 0;

        if (this.isRefund() || this.transaction_type === TRANSACTION_TYPES_ENUM.Chargeback) {
            value = this.transaction_amount + this.getTotalFees();
        } else {
            value = this.transaction_amount - this.getTotalFees();
        }

        return value;
    }
    getRefundAmount(): number {
        if (this.isRefund()) {
            return this.transaction_amount || 0;
        } else {
            return 0;
        }
    }
}
